import React, { useContext, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { ThemeContext } from "styled-components/macro";

import { ACTIVATE_ITEM } from "../../store/actions/configurationActions";
import { getShowPrice } from "../../store/sagas/selectors";
import GaReporter from "../../utilities/gaReporter";
import Constants from "../../constants";
import { Item } from "../../models/item";

import { useBreakpoint } from "../../utilities/media-js-breakpoints";

const ItemsContainer = styled.div`
    display: flex;
    flex-flow: row wrap;
    position: relative;
`;

const ItemContainer = styled.div<ItemContainerProps>`
    flex: 0 1 auto;
    
    width: ${({ itemWidth }) => `${itemWidth}px`};
    height: ${({ cellHeight }) => `${cellHeight}px`};
    margin: 0px;
    margin-right: ${({ itemMarginRight }) => `${itemMarginRight}px`};
    margin-bottom: ${({ itemMarginBottom }) => `${itemMarginBottom}px`};
    flex-flow: column nowrap;
    align-items: center;
    justify-content: flex-start
    display: flex;
    position: absolute;

    &.large {
        position: relative;
        height: auto;
        ${({ theme }) =>
            theme.bp.xs`
                width: calc(100% - 6px);
                padding-left: 6px;
                `};
    }
`;

interface ItemContainerProps {
    cellHeight: number;
    itemWidth: number;
    itemMarginRight: number;
    itemMarginBottom: number;
}

const ItemImage = styled.img`
    cursor: pointer;
    object-fit: cover;
    background: rgba(255, 255, 255, 1);
    width: 100%;
    border-radius: 4px;
    box-shadow: none;
    transition: all 0.2s;

    @media (hover) {
        &:hover {
            box-shadow: 0 0 0px 4px var(--primary-hover-color);
            background: rgba(255, 255, 255, 0.8);
        }
    }
    margin-bottom: 6px;
`;

const ItemText = styled.p`
    font-size: 12px;
    flex: 1 0 auto;
    overflow: hidden;
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: break-spaces;
    width: 100%;
    text-align: left;
    font-family: ${props => props.theme.fontFamily};
    color: ${props => props.theme.textColorSecondary};
    margin-top: 0;
    margin-bottom: 0;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    word-break: break-word;

    &.large {
        ${({ theme }) =>
            theme.bp.xs`
                    font-size: 14px;
                `}
        ${({ theme }) =>
            theme.bp.md`
                    font-size: 12px;
                `}
    }
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const ItemPrice = styled.div`
    font-size: 13px;
    top: 2px;
    pointer-event: none;
    border-radius: 12px;
    color: #fff;
    padding: 2px 8px;
    margin: 4px 0px 8px;
    background-color: rgb(153, 153, 153);
    text-align: center;
    white-space: nowrap;
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const Divider = styled.div`
    width: 100%;
    border-bottom: solid thin #ccc;
    margin: 5px 0px;
`;

export const ItemsList = ({
    items,
    activeItems,
    largeIcons,
    onItemSelected,
    menuCenterY,
    menuWidth,
}: {
    items: Item[];
    activeItems: Item[];
    largeIcons: Boolean;
    onItemSelected: (item: Item) => void;
    menuCenterY: number;
    menuWidth: number;
}) => {
    const dispatch = useDispatch();
    const theme = useContext(ThemeContext);
    const showPrice = useSelector(getShowPrice);
    const containerRef = useRef<HTMLDivElement>(null);
    const breakpoints = useBreakpoint();
    const isHighRes = breakpoints.xxxl;

    const itemWidth = isHighRes ? Constants.highResMenuItemWidth : Constants.menuItemWidth;
    const itemHeight = isHighRes ? Constants.highResMenuItemHeight : Constants.menuItemHeight;
    const priceHeight = isHighRes ? Constants.highResMenuItemPriceHeight : Constants.menuItemPriceHeight;
    const itemMarginRight = isHighRes ? Constants.highResMenuItemMarginRight : Constants.menuItemMarginRight;
    const itemMarginBottom = isHighRes ? Constants.highResMenuItemmargeinBottom : Constants.menuItemmargeinBottom;

    const onItemClick = (item: Item) => {
        dispatch({ type: ACTIVATE_ITEM, selectedItem: item.id });

        if (!item.isSelected) {
            GaReporter.trackItemActivation(item, "Item menu", showPrice);
        }

        onItemSelected(item);
    };

    const currentActiveItems = activeItems;
    const totalItems = activeItems.concat(items);
    const priceSpace = totalItems?.some(item => item.price?.text) ? priceHeight : 0;
    const cellWidth = itemWidth + itemMarginRight;
    const cellHeight = itemHeight + priceSpace + itemMarginBottom;
    const columns = Math.floor((menuWidth - 42) / cellWidth);
    const rows = Math.ceil(totalItems?.length / columns || 1);
    const renderLimit = columns * 10;
    let renderStart: number;
    let renderEnd: number;
    if (largeIcons) {
        renderStart = 0;
        renderEnd = totalItems?.length || 0;
    } else {
        renderStart = Math.floor((menuCenterY / cellHeight) * columns - renderLimit / 2) - activeItems.length;
        renderEnd = Math.ceil((menuCenterY / cellHeight) * columns + renderLimit / 2) - activeItems.length;
        if (renderStart < 0) {
            renderEnd += -renderStart;
            renderStart = 0;
        }
        if (renderEnd > totalItems?.length) {
            renderStart = Math.max(renderStart - (renderEnd - totalItems.length), 0);
            renderEnd = totalItems.length;
        }
    }

    const xPadding = Math.floor((menuWidth - 42 - cellWidth * columns) / 2);
    const yPadding = 10;

    const renderPriceTag = (item: any) => {
        if (showPrice && item.price?.text) {
            return <ItemPrice>{item.price?.text}</ItemPrice>;
        }
        return <></>;
    };

    const getText = (item: any) => {
        const text = item.shortText ? item.shortText.replace(/\\n/g, "\n") : "";
        const textArr = text.split("\n");
        let rowIndex = 0;
        return (
            <div title={text}>
                {textArr.map((v: string) => {
                    rowIndex += 1;
                    return (
                        <ItemText key={rowIndex} className={largeClass}>
                            {v}
                        </ItemText>
                    );
                })}
            </div>
        );
    };

    const drawItem = (item: Item, index: any) => {
        return (
            <ItemContainer
                className={largeClass}
                key={item.id}
                cellHeight={cellHeight}
                itemWidth={itemWidth}
                itemMarginRight={itemMarginRight}
                itemMarginBottom={itemMarginBottom}
                style={
                    largeIcons
                        ? undefined
                        : {
                              left: `${(index % columns) * cellWidth + xPadding}px`,
                              top: `${Math.floor(index / columns) * cellHeight + yPadding}px`,
                          }
                }
            >
                <ItemImage
                    data-testid="menu-item"
                    data-item-id={item.id}
                    data-selected={item.isSelected ? "true" : "false"}
                    style={{
                        boxShadow: item.isSelected ? `0 0 0 4px ${theme.sideMenu.itemImage.borderColor}` : undefined,
                        background: item.isSelected ? `rgba(255,255,255,0.7)` : undefined,
                    }}
                    onClick={() => onItemClick(item)}
                    onKeyDown={() => {}}
                    alt="Menu Item"
                    src={item.thumbnail || undefined}
                />
                {renderPriceTag(item)}
                {getText(item)}
            </ItemContainer>
        );
    };

    const largeClass = largeIcons ? "large" : undefined;
    return (
        <ItemsContainer
            ref={containerRef}
            style={
                largeIcons
                    ? undefined
                    : {
                          width: columns * cellWidth + 2,
                          height: rows * cellHeight + yPadding * 2,
                      }
            }
        >
            <>
                <div
                    style={{
                        width: columns * cellWidth + 2,
                        height: largeIcons
                            ? Math.ceil(currentActiveItems.length / columns) * cellHeight + yPadding * 2
                            : Math.ceil(currentActiveItems.length / columns) * cellHeight + yPadding,
                        position: "relative",
                    }}
                >
                    {currentActiveItems?.map((activeItem: Item, index) =>
                        index >= renderStart && index < renderEnd ? drawItem(activeItem, index) : null
                    )}
                </div>
                {currentActiveItems?.length > 0 ? <Divider /> : null}
                <div
                    style={{
                        width: columns * cellWidth + 2,
                        height: largeIcons
                            ? rows * (cellHeight + yPadding) * 2
                            : Math.ceil(items?.length / columns) * cellHeight + yPadding,
                        position: "relative",
                    }}
                >
                    {items?.map((item: Item, index) =>
                        index >= renderStart && index < renderEnd ? drawItem(item, index) : null
                    )}
                </div>
            </>
        </ItemsContainer>
    );
};
